import React, { useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { Loader2, Upload, Download, CheckCircle2, XCircle, FileSpreadsheet } from 'lucide-react';
import { formatINR } from './payoutUtils';

// Excel settlement: upload -> parse/match/validate preview -> admin confirms -> only valid payouts complete.
export const ExcelSettlementDialog = ({ open, onOpenChange, onDone }) => {
  const { api } = useAuth();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => { setBatch(null); setResult(null); };
  const close = (o) => { if (!o) reset(); onOpenChange(o); };

  const template = async () => {
    try {
      const r = await api.get('/payout-settlements/excel/template', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = 'awadhpay_settlement_template.xlsx'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Template download failed'); }
  };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    setBusy(true);
    try { const r = await api.post('/payout-settlements/excel/validate', fd, { headers: { 'Content-Type': 'multipart/form-data' } }); setBatch(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Validation failed'); }
    finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const confirm = async () => {
    setBusy(true);
    try { const r = await api.post(`/payout-settlements/excel/${batch.batch_id}/confirm`, {}); setResult(r.data); toast.success(`${r.data.completed} payout(s) completed`); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Confirmation failed'); }
    finally { setBusy(false); }
  };

  const s = batch?.summary;
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl rounded-sm max-h-[92vh] overflow-y-auto" data-testid="excel-settlement-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><FileSpreadsheet className="h-5 w-5" />Excel Settlement</DialogTitle>
          <DialogDescription>Upload a settlement sheet (Platform Order No, Account Name, Bank Account No, Bank IFSC, Order Amount, Order Status, UTR, Transaction Time, Remark, Image). Rows are matched and validated first — nothing completes until you confirm.</DialogDescription>
        </DialogHeader>

        {!batch && (
          <div className="space-y-3">
            <div className="p-8 border border-dashed border-border/60 rounded-sm text-center">
              <input ref={ref} type="file" accept=".xlsx,.csv" className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="excel-file-input" />
              <Button onClick={() => ref.current?.click()} disabled={busy} className="rounded-sm" data-testid="excel-upload-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Upload Excel / CSV</Button>
              <p className="text-xs text-muted-foreground mt-2">.xlsx or .csv · Max 10MB · Embedded images are extracted as proof</p>
            </div>
            <Button variant="ghost" size="sm" className="rounded-sm text-xs" onClick={template} data-testid="excel-template-btn"><Download className="h-3.5 w-3.5 mr-1" />Download sample template</Button>
          </div>
        )}

        {batch && !result && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-sm" data-testid="excel-summary">
              {[['Rows', s.rows], ['Matched', s.matched], ['Amount Match', s.amount_match], ['UTR Valid', s.utr_valid], ['Duplicate UTR', s.duplicate_utr, s.duplicate_utr > 0 && 'text-red-600'], ['Failed', s.failed, s.failed > 0 && 'text-red-600']].map(([k, v, cls]) => (
                <div key={k} className="p-2 rounded-sm border border-border/50"><p className="text-[10px] uppercase text-muted-foreground">{k}</p><p className={`font-semibold ${cls || ''}`} data-testid={`excel-${k.toLowerCase().replace(/\s+/g, '-')}`}>{v}</p></div>
              ))}
            </div>
            <div className="flex items-center justify-between p-3 rounded-sm bg-green-500/5 border border-green-500/30 text-sm"><span>Completable payouts</span><span className="font-semibold" data-testid="excel-completable">{s.completable_payouts} · {formatINR(s.completable_amount)}</span></div>
            <div className="rounded-sm border border-border/50 overflow-x-auto">
              <table className="w-full text-xs min-w-[760px]">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground"><tr><th className="text-left p-2">Row</th><th className="text-left p-2">Order</th><th className="text-left p-2">Account</th><th className="text-right p-2">Amount</th><th className="text-left p-2">UTR</th><th className="text-left p-2">Checks</th></tr></thead>
                <tbody>
                  {batch.rows.map(r => (
                    <tr key={r.row} className={`border-t border-border/40 ${r.valid ? '' : 'bg-red-500/5'}`} data-testid={`excel-row-${r.row}`}>
                      <td className="p-2 text-muted-foreground">{r.row}</td>
                      <td className="p-2 font-mono">{r.order_id || r.order_no || '—'}{r.order_id && r.order_no && r.order_id !== r.order_no && <span className="block text-[10px] text-muted-foreground">ref {r.order_no}</span>}</td>
                      <td className="p-2">{r.account_name}<span className="block text-[10px] text-muted-foreground font-mono">{r.account_no ? `•••• ${r.account_no.slice(-4)}` : ''} {r.ifsc}</span></td>
                      <td className="p-2 text-right font-mono">{r.amount != null ? formatINR(r.amount, 2) : '—'}{r.settlement_total != null && r.settlement_total !== r.amount && <span className="block text-[10px] text-muted-foreground">Σ {formatINR(r.settlement_total, 2)}</span>}</td>
                      <td className="p-2 font-mono">{r.utr || '—'}{r.embedded_proofs?.length > 0 && <span className="block text-[10px] text-green-600">{r.embedded_proofs.length} image proof</span>}</td>
                      <td className="p-2">
                        {r.valid ? <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Matched · Amount verified · UTR unique</span>
                          : r.issues.map((i, k) => <span key={k} className="text-red-600 flex items-center gap-1"><XCircle className="h-3 w-3 shrink-0" />{i}</span>)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-sm" onClick={reset} data-testid="excel-reset-btn">Upload another file</Button>
              <Button className="rounded-sm bg-green-600 hover:bg-green-700 text-white" disabled={busy || s.completable_payouts === 0} onClick={confirm} data-testid="excel-confirm-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Complete {s.completable_payouts} Valid Payout(s)</Button>
            </DialogFooter>
          </div>
        )}

        {result && (
          <div className="space-y-3" data-testid="excel-result">
            <div className="p-3 rounded-sm border border-green-500/30 bg-green-500/5 text-sm"><span className="font-semibold">{result.completed} completed</span> · {result.failed} failed · {formatINR(result.total_amount)}</div>
            {result.results.map(r => <div key={r.payout_id} className="flex justify-between text-sm py-1.5 border-b border-border/40"><span className="font-mono">{r.order_id}</span>{r.status === 'completed' ? <span className="text-green-600">Completed · {r.settlement_ids.length} receipt(s)</span> : <span className="text-red-600 text-xs">{r.reason}</span>}</div>)}
            <DialogFooter><Button className="rounded-sm" onClick={() => close(false)} data-testid="excel-close-btn">Close</Button></DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
